import { useCallback, useMemo, useState } from 'react'
import { RepoSelectModal, UploadedRepo } from '~/components/repo-selection/LegacyRepoSelectModal'
import { Selector, SelectItem } from '~/components/common/Selector'
import { Typography } from '~/components/common/Typography'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { useRepositoryBranches } from '~/hooks/useCodeContext'
import {
  CommandType,
  IntegrationPlatform,
  integrationPlatformFromJSON,
  integrationPlatformToJSON
} from '~/protos/agents'
import { bridge } from '~/services/bridge'
import { ProjectSelectionOption } from '~/store/taskFormSlice'
import { RepositoryBranch } from '~/types/Conversations'
import { getIntegrationPlatformItems } from '~/utils/agentUtils'
import { extractFileDisplayName } from '~/utils/repositoryUtils'
import { MetadataFileSelectModal, UploadedMetadataFile } from './MetadataFileSelectModal'
import { MuleRefactorFlowLayout } from '~/components/mule-refactor/MuleRefactorFlowLayout'
import { EndpointSelectionLoading } from '~/components/mule-refactor/slides/EndpointSelectionSlide'
import { getSlideConfigById } from '~/components/mule-refactor/slideConfig'

// Purple filled circular checkmark icon for selected state
const SelectedCheckmark = () => (
  <div className='shrink-0 mt-0.5'>
    <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <circle cx='10' cy='10' r='10' fill='#65558F' />
      <path
        d='M6 10L9 13L14 7'
        stroke='white'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  </div>
)

interface MuleRefactorRepoSelectionScreenProps {
  onClose: () => void
  repositories: RepositoryBranch[]
  onSave: (payload: {
    legacyRepos: RepositoryBranch[]
    legacyOption: ProjectSelectionOption
    legacyS3Paths: string[]
    mulesoftRepos: RepositoryBranch[]
    mulesoftOption?: ProjectSelectionOption
    mulesoftS3Paths: string[]
    platform: IntegrationPlatform
    metadataS3Paths?: string[]
  }) => void
  /** True while the parent is creating the task; locks the proceed button. */
  isSubmitting?: boolean
}

export function MuleRefactorRepoSelectionScreen({
  onClose,
  repositories,
  onSave,
  isSubmitting = false
}: MuleRefactorRepoSelectionScreenProps) {
  const isPlugin = bridge.isInPlugin()
  // Legacy repository state (required)
  const [legacyRepoModalOpen, setLegacyRepoModalOpen] = useState(false)
  const [legacySelectedRepos, setLegacySelectedRepos] = useState<RepositoryBranch[]>([])
  const [legacyUploadedRepos, setLegacyUploadedRepos] = useState<UploadedRepo[]>([])

  // MuleSoft repository state (optional)
  const [mulesoftRepoModalOpen, setMulesoftRepoModalOpen] = useState(false)
  const [mulesoftSelectedRepos, setMulesoftSelectedRepos] = useState<RepositoryBranch[]>([])
  const [mulesoftUploadedRepos, setMulesoftUploadedRepos] = useState<UploadedRepo[]>([])

  // Metadata files state (optional)
  const [metadataModalOpen, setMetadataModalOpen] = useState(false)
  const [metadataUploadedFiles, setMetadataUploadedFiles] = useState<UploadedMetadataFile[]>([])

  // Platform selection
  const [platform, setPlatform] = useState<IntegrationPlatform>(IntegrationPlatform.TIBCO)

  const { currentWorkspace } = useWorkspaceContext()
  const command = CommandType.MULE_REFACTORING
  const { repos, getBranches } = useRepositoryBranches(command)

  // Convert repos to the format needed for RepoSelectModal
  const selectableRepos = useMemo(
    () =>
      repos.map(repo => ({
        conn_type: repo.conn_type,
        index_name: repo.index_name,
        label: extractFileDisplayName(`${repo.conn_type}:${repo.index_name}`)
      })),
    [repos]
  )

  // Handle legacy repository modal confirmation
  // Uploads are now handled inside the modal, so this is simple
  const handleLegacyRepoConfirm = useCallback(
    (data: { repositoryBranches: RepositoryBranch[]; uploadedRepos: UploadedRepo[] }) => {
      setLegacySelectedRepos(data.repositoryBranches)
      setLegacyUploadedRepos(data.uploadedRepos)
      setLegacyRepoModalOpen(false)
    },
    []
  )

  // Handle MuleSoft repository modal confirmation
  // Uploads are now handled inside the modal, so this is simple
  const handleMulesoftRepoConfirm = useCallback(
    (data: { repositoryBranches: RepositoryBranch[]; uploadedRepos: UploadedRepo[] }) => {
      setMulesoftSelectedRepos(data.repositoryBranches)
      setMulesoftUploadedRepos(data.uploadedRepos)
      setMulesoftRepoModalOpen(false)
    },
    []
  )

  // Handle metadata files modal confirmation
  const handleMetadataFilesConfirm = useCallback(
    (data: { uploadedFiles: UploadedMetadataFile[] }) => {
      setMetadataUploadedFiles(data.uploadedFiles)
      setMetadataModalOpen(false)
    },
    []
  )

  const isLegacyRepoSelected = legacySelectedRepos.length > 0 || legacyUploadedRepos.length > 0
  const isMulesoftRepoSelected =
    mulesoftSelectedRepos.length > 0 || mulesoftUploadedRepos.length > 0
  const isPlatformSelected = platform !== IntegrationPlatform.UNRECOGNIZED
  const canSave = isLegacyRepoSelected && isPlatformSelected

  // Memoized display data for legacy repos - show all repos with separate name/branch fields
  const legacyDisplayData = useMemo(() => {
    return [
      ...legacySelectedRepos.map(repo => ({
        id: `git-${repo.repository}-${repo.branch}`,
        name: extractFileDisplayName(repo.repository),
        branch: repo.branch || undefined
      })),
      ...legacyUploadedRepos.map(repo => ({
        id: `upload-${repo.id}`,
        name: repo.name,
        branch: undefined // Uploaded repos don't have branches
      }))
    ]
  }, [legacySelectedRepos, legacyUploadedRepos])

  // Memoized display data for mulesoft repos - show all repos with separate name/branch fields
  const mulesoftDisplayData = useMemo(() => {
    return [
      ...mulesoftSelectedRepos.map(repo => ({
        id: `git-${repo.repository}-${repo.branch}`,
        name: extractFileDisplayName(repo.repository),
        branch: repo.branch || undefined
      })),
      ...mulesoftUploadedRepos.map(repo => ({
        id: `upload-${repo.id}`,
        name: repo.name,
        branch: undefined // Uploaded repos don't have branches
      }))
    ]
  }, [mulesoftSelectedRepos, mulesoftUploadedRepos])

  // Memoized display data for metadata files - show all files
  const metadataDisplayData = useMemo(() => {
    return metadataUploadedFiles.map(file => ({
      id: `file-${file.id}`,
      name: file.name
    }))
  }, [metadataUploadedFiles])

  // Memoized platform items and dropdown width calculation
  const { platformItems, platformDropdownMinWidth } = useMemo(() => {
    const items = getIntegrationPlatformItems([
      'UI_PATH_EXP_V1',
      'APIGEE_POLICY_V1',
      'NODE_JS',
      'SAP_PI_PO',
      'FLEX_GATEWAY_RUST_POLICY'
    ])
    // Add MULESOFT option explicitly for this screen
    if (!items.some(item => item.key === 'MULESOFT')) {
      items.push({ label: 'Mulesoft', key: 'MULESOFT' })
    }
    // Calculate min width based on longest label
    // From Select.tsx: trigger needs px-2 (8px each side) + ml-2 (8px) + w-5 (20px arrow) = 44px overhead
    // Character width: ~7.5px average for 14px Geist Medium
    const longestLabelLength = Math.max(...items.map(item => item.label.length))
    const minWidth = Math.ceil(longestLabelLength * 8 + 44)
    return { platformItems: items, platformDropdownMinWidth: minWidth }
  }, [])

  const handleSave = useCallback(() => {
    if (!canSave) return

    // Determine legacy option
    const legacyOption: ProjectSelectionOption =
      legacySelectedRepos.length > 0
        ? 'codeContext'
        : legacyUploadedRepos.length > 0
          ? 'upload'
          : 'none'

    // Get all legacy S3 paths (type-safe)
    const legacyS3Paths = legacyUploadedRepos
      .map(repo => repo.s3Path)
      .filter((path): path is string => Boolean(path))

    // Determine MuleSoft option
    const mulesoftOption: ProjectSelectionOption | undefined =
      mulesoftSelectedRepos.length > 0
        ? 'codeContext'
        : mulesoftUploadedRepos.length > 0
          ? 'upload'
          : undefined

    // Get all mulesoft S3 paths (type-safe)
    const mulesoftS3Paths = mulesoftUploadedRepos
      .map(repo => repo.s3Path)
      .filter((path): path is string => Boolean(path))

    // Get all metadata S3 paths (type-safe)
    const metadataS3Paths = metadataUploadedFiles
      .map(file => file.s3Path)
      .filter((path): path is string => Boolean(path))

    onSave({
      legacyRepos: legacySelectedRepos,
      legacyOption,
      legacyS3Paths,
      mulesoftRepos: mulesoftSelectedRepos,
      mulesoftOption,
      mulesoftS3Paths,
      platform,
      metadataS3Paths
    })
  }, [
    canSave,
    legacySelectedRepos,
    legacyUploadedRepos,
    mulesoftSelectedRepos,
    mulesoftUploadedRepos,
    metadataUploadedFiles,
    platform,
    onSave
  ])

  // The instant the user submits, optimistically swap to the endpoint-identification
  // skeleton (the same view the in-task flow renders) instead of leaving them on a
  // frozen repo screen. This component stays mounted, so the user's repo/platform
  // selections are preserved if submission fails and we fall back to the form.
  if (isSubmitting) {
    return (
      <MuleRefactorFlowLayout
        currentStepNumber={1}
        proceedButtonText={getSlideConfigById('selectEndpoints')?.buttonText}
        isProceedDisabled
      >
        <EndpointSelectionLoading />
      </MuleRefactorFlowLayout>
    )
  }

  return (
    <>
      <MuleRefactorFlowLayout
        currentStepNumber={0}
        proceedButtonText='Next: Select Endpoints'
        isProceedDisabled={!canSave}
        onProceed={handleSave}
      >
        {/* Scrollable Container */}
        <div className='w-full h-full overflow-y-auto'>
          <div className={`flex justify-center w-full pb-20 ${isPlugin ? 'px-3' : 'px-[60px]'}`}>
            <div
              className={`task-form-body flex-grow w-full flex flex-col gap-12 ${isPlugin ? 'max-w-full min-w-0' : 'max-w-[720px] min-w-[600px]'}`}
            >
              {/* Modal Header */}
              <div className='pt-[60px] flex flex-col items-center gap-4'>
                <Typography
                  fontFamily='geist'
                  fontSize='titleLg'
                  fontWeight='bold'
                  className='text-black text-center'
                >
                  Select Source Repository
                </Typography>
              </div>

              {/* Platform Selection Card - Always visible at top */}
              <div
                className='rounded-[24px] px-6 py-4 flex flex-col gap-3'
                style={{ background: 'rgba(189,189,189,0.08)' }}
              >
                {/* Select source integration platform */}
                <div className='flex items-center justify-between w-full'>
                  <div className='flex items-center gap-4'>
                    <svg
                      width='18'
                      height='18'
                      viewBox='0 0 18 18'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      className='shrink-0'
                    >
                      <path
                        d='M1.49805 3.75H7.49805V8.25H1.49805V3.75ZM2.99805 5.25V6.75H5.99805V5.25H2.99805ZM1.49805 9.75H16.498V14.25H1.49805V9.75ZM2.99805 11.25V12.75H14.998V11.25H2.99805Z'
                        fill='black'
                        fillOpacity='0.88'
                      />
                    </svg>
                    <Typography
                      fontFamily='geist'
                      fontSize='bodySm'
                      fontWeight='medium'
                      style={{ color: 'rgba(0,0,0,0.45)', lineHeight: '22px' }}
                    >
                      Source Integration Platform
                    </Typography>
                  </div>
                  <div style={{ minWidth: platformDropdownMinWidth, maxWidth: 360 }}>
                    <Selector
                      placeholder='Select source platform'
                      value={integrationPlatformToJSON(platform)}
                      onSelectionChange={value => {
                        setPlatform(integrationPlatformFromJSON(value))
                      }}
                      className='w-full'
                      triggerClassName='px-2 py-1 bg-transparent border-none shadow-none h-auto min-h-0 rounded-lg hover:bg-[rgba(0,0,0,0.04)]'
                      valueClassName='text-[rgba(0,0,0,0.88)] font-medium text-sm block !max-w-none !text-right'
                    >
                      {platformItems.map(item => (
                        <SelectItem key={item.key} value={item.key}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </Selector>
                  </div>
                </div>

                {/* Divider */}
                <div className='h-px w-full' style={{ background: 'rgba(0,0,0,0.1)' }} />

                {/* Legacy Platform Repository Section */}
                <div
                  className='group flex items-start gap-4 cursor-pointer py-1 transition-colors'
                  onClick={() => setLegacyRepoModalOpen(true)}
                >
                  <svg
                    width='18'
                    height='18'
                    viewBox='0 0 18 18'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                    className='shrink-0 mt-0.5 text-black group-hover:text-[#5B5BD5] transition-colors'
                  >
                    <path
                      d='M9 4.5V13.5M4.5 9H13.5'
                      stroke='currentColor'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  <div className='flex flex-1 flex-col gap-1'>
                    {isLegacyRepoSelected ? (
                      <>
                        <Typography
                          fontFamily='geist'
                          fontSize='bodySm'
                          fontWeight='medium'
                          className='text-[rgba(0,0,0,0.45)] group-hover:text-[#5B5BD5] transition-colors'
                          style={{ lineHeight: '22px' }}
                        >
                          Legacy platform repository (required)
                        </Typography>
                        {/* Display all repos in scrollable container */}
                        <div className='flex flex-col gap-2 max-h-[184px] overflow-y-auto pr-1'>
                          {legacyDisplayData.map((repo, index) => (
                            <div key={repo.id} className='flex flex-col py-1'>
                              <Typography
                                fontFamily='geist'
                                fontSize='titleSm'
                                fontWeight='bold'
                                className='text-tokens-neutral-text-primary'
                              >
                                {`Repository ${index + 1}: ${repo.name}`}
                              </Typography>
                              {repo.branch && (
                                <div className='flex items-center gap-2 mt-1'>
                                  <svg
                                    width='16'
                                    height='16'
                                    viewBox='0 0 16 16'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                    className='shrink-0'
                                  >
                                    <path
                                      d='M6 3V9C6 9.53043 6.21071 10.0391 6.58579 10.4142C6.96086 10.7893 7.46957 11 8 11H10'
                                      stroke='#8E8E8E'
                                      strokeWidth='1.5'
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                    />
                                    <circle
                                      cx='12'
                                      cy='11'
                                      r='2'
                                      stroke='#8E8E8E'
                                      strokeWidth='1.5'
                                    />
                                    <circle
                                      cx='6'
                                      cy='3'
                                      r='2'
                                      stroke='#8E8E8E'
                                      strokeWidth='1.5'
                                    />
                                  </svg>
                                  <Typography
                                    fontFamily='geist'
                                    fontSize='labelSm'
                                    style={{ color: 'rgba(0,0,0,0.45)', lineHeight: '14px' }}
                                  >
                                    {repo.branch}
                                  </Typography>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <Typography
                          fontFamily='geist'
                          fontSize='bodySm'
                          fontWeight='medium'
                          className='text-[rgba(0,0,0,0.45)] group-hover:text-[#5B5BD5] transition-colors'
                          style={{ lineHeight: '22px' }}
                        >
                          Legacy platform repository (required)
                        </Typography>
                        <Typography
                          fontFamily='geist'
                          fontSize='labelSm'
                          style={{ color: 'rgba(0,0,0,0.45)', lineHeight: 'normal' }}
                        >
                          Connect your Git repository
                        </Typography>
                      </>
                    )}
                  </div>
                  {isLegacyRepoSelected && isPlatformSelected && <SelectedCheckmark />}
                </div>
              </div>

              {/* Additional Metadata Section - Separate Card */}
              <div
                className={`group border border-dashed border-[#eaeaea] hover:border-solid hover:border-[#eaeaea] rounded-[16px] cursor-pointer ${metadataUploadedFiles.length > 0 ? 'min-h-[72px]' : 'h-[72px]'} flex items-center transition-all`}
                style={{ background: 'rgba(189,189,189,0.08)' }}
                onClick={() => setMetadataModalOpen(true)}
              >
                <div className='flex items-start gap-4 p-4 w-full'>
                  <div className='flex items-center justify-center shrink-0 w-6 h-6 mt-0.5'>
                    <svg
                      width='18'
                      height='18'
                      viewBox='0 0 18 18'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      className='shrink-0'
                    >
                      <path
                        d='M3 4.5V9C3 9 3 11.25 8.25 11.25C13.5 11.25 13.5 9 13.5 9V4.5'
                        stroke='#8E8E8E'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M8.25 2.25C13.5 2.25 13.5 4.5 13.5 4.5C13.5 4.5 13.5 6.75 8.25 6.75C3 6.75 3 4.5 3 4.5C3 4.5 3 2.25 8.25 2.25Z'
                        stroke='#8E8E8E'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M8.25 15.75C3 15.75 3 13.5 3 13.5V9'
                        stroke='#8E8E8E'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M11.25 15.75V14.25'
                        stroke='#8E8E8E'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                      />
                      <path
                        d='M13.5 15.75V12.75'
                        stroke='#8E8E8E'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                      />
                      <path
                        d='M15.75 15.75V11.25'
                        stroke='#8E8E8E'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                      />
                    </svg>
                  </div>
                  <div className='flex-1 flex flex-col gap-1'>
                    {metadataUploadedFiles.length > 0 ? (
                      <>
                        <Typography
                          fontFamily='geist'
                          fontSize='bodySm'
                          fontWeight='medium'
                          className='text-[rgba(0,0,0,0.45)] group-hover:text-[#5B5BD5] transition-colors'
                          style={{ lineHeight: '22px' }}
                        >
                          Additional Metadata (optional)
                        </Typography>
                        {/* Display all files in scrollable container */}
                        <div className='flex flex-col gap-2 max-h-[184px] overflow-y-auto pr-1'>
                          {metadataDisplayData.map(file => (
                            <div key={file.id} className='flex flex-col py-1'>
                              <Typography
                                fontFamily='geist'
                                fontSize='bodySm'
                                fontWeight='medium'
                                style={{ color: 'rgba(0,0,0,0.88)', lineHeight: '18px' }}
                              >
                                {file.name}
                              </Typography>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <Typography
                          fontFamily='geist'
                          fontSize='bodySm'
                          fontWeight='medium'
                          className='text-[rgba(0,0,0,0.45)] group-hover:text-[#5B5BD5] transition-colors'
                          style={{ lineHeight: '22px' }}
                        >
                          Additional Metadata (optional)
                        </Typography>
                        <Typography
                          fontFamily='geist'
                          fontSize='labelSm'
                          style={{ color: 'rgba(0,0,0,0.45)', lineHeight: 'normal' }}
                        >
                          Upload files containing information about the legacy repositories
                        </Typography>
                      </>
                    )}
                  </div>
                  {metadataUploadedFiles.length > 0 && <SelectedCheckmark />}
                </div>
              </div>

              {/* MuleSoft Code Section - Separate Card */}
              <div
                className={`group border border-dashed border-[#eaeaea] hover:border-solid hover:border-[#eaeaea] rounded-[16px] cursor-pointer ${isMulesoftRepoSelected ? 'min-h-[72px]' : 'h-[72px]'} flex items-center transition-all`}
                style={{ background: 'rgba(189,189,189,0.08)' }}
                onClick={() => setMulesoftRepoModalOpen(true)}
              >
                <div className='flex items-start gap-4 p-4 w-full'>
                  <div className='flex items-center justify-center shrink-0 w-6 h-6 mt-0.5'>
                    <svg
                      width='18'
                      height='18'
                      viewBox='0 0 18 18'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      className='shrink-0 text-black group-hover:text-[#5B5BD5] transition-colors'
                    >
                      <path
                        d='M9 4.5V13.5M4.5 9H13.5'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </div>
                  <div className='flex-1 flex flex-col gap-1'>
                    {isMulesoftRepoSelected ? (
                      <>
                        <Typography
                          fontFamily='geist'
                          fontSize='bodySm'
                          fontWeight='medium'
                          className='text-[rgba(0,0,0,0.45)] group-hover:text-[#5B5BD5] transition-colors'
                          style={{ lineHeight: '22px' }}
                        >
                          MuleSoft Code (optional)
                        </Typography>
                        {/* Display all repos in scrollable container */}
                        <div className='flex flex-col gap-2 max-h-[184px] overflow-y-auto pr-1'>
                          {mulesoftDisplayData.map((repo, index) => (
                            <div key={repo.id} className='flex flex-col py-1'>
                              <Typography
                                fontFamily='geist'
                                fontSize='titleSm'
                                fontWeight='bold'
                                className='text-tokens-neutral-text-primary'
                              >
                                {`Repository ${index + 1}: ${repo.name}`}
                              </Typography>
                              {repo.branch && (
                                <div className='flex items-center gap-2 mt-1'>
                                  <svg
                                    width='16'
                                    height='16'
                                    viewBox='0 0 16 16'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                    className='shrink-0'
                                  >
                                    <path
                                      d='M6 3V9C6 9.53043 6.21071 10.0391 6.58579 10.4142C6.96086 10.7893 7.46957 11 8 11H10'
                                      stroke='#8E8E8E'
                                      strokeWidth='1.5'
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                    />
                                    <circle
                                      cx='12'
                                      cy='11'
                                      r='2'
                                      stroke='#8E8E8E'
                                      strokeWidth='1.5'
                                    />
                                    <circle
                                      cx='6'
                                      cy='3'
                                      r='2'
                                      stroke='#8E8E8E'
                                      strokeWidth='1.5'
                                    />
                                  </svg>
                                  <Typography
                                    fontFamily='geist'
                                    fontSize='labelSm'
                                    style={{ color: 'rgba(0,0,0,0.45)', lineHeight: '14px' }}
                                  >
                                    {repo.branch}
                                  </Typography>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <Typography
                          fontFamily='geist'
                          fontSize='bodySm'
                          fontWeight='medium'
                          className='text-[rgba(0,0,0,0.45)] group-hover:text-[#5B5BD5] transition-colors'
                          style={{ lineHeight: '22px' }}
                        >
                          MuleSoft Code (optional)
                        </Typography>
                        <Typography
                          fontFamily='geist'
                          fontSize='labelSm'
                          style={{ color: 'rgba(0,0,0,0.45)', lineHeight: 'normal' }}
                        >
                          Add your MuleSoft code as supplement source
                        </Typography>
                      </>
                    )}
                  </div>
                  {isMulesoftRepoSelected && <SelectedCheckmark />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </MuleRefactorFlowLayout>

      {/* Legacy Repository Selection Modal */}
      <RepoSelectModal
        isOpen={legacyRepoModalOpen}
        onClose={() => setLegacyRepoModalOpen(false)}
        onConfirm={handleLegacyRepoConfirm}
        repositories={selectableRepos}
        getBranches={getBranches}
        title='Select Legacy Repository'
        initialSelectedRepos={legacySelectedRepos}
        initialUploadedRepos={legacyUploadedRepos}
        workspaceUuid={currentWorkspace?.uuid}
      />

      {/* MuleSoft Repository Selection Modal */}
      <RepoSelectModal
        isOpen={mulesoftRepoModalOpen}
        onClose={() => setMulesoftRepoModalOpen(false)}
        onConfirm={handleMulesoftRepoConfirm}
        repositories={selectableRepos}
        getBranches={getBranches}
        title='Select MuleSoft Reference Code'
        initialSelectedRepos={mulesoftSelectedRepos}
        initialUploadedRepos={mulesoftUploadedRepos}
        workspaceUuid={currentWorkspace?.uuid}
      />

      {/* Metadata Files Selection Modal */}
      <MetadataFileSelectModal
        isOpen={metadataModalOpen}
        onClose={() => setMetadataModalOpen(false)}
        onConfirm={handleMetadataFilesConfirm}
        workspaceUuid={currentWorkspace?.uuid}
        initialFiles={metadataUploadedFiles}
      />
    </>
  )
}

export default MuleRefactorRepoSelectionScreen
